import { useState, useEffect, useMemo } from 'react'
import { generateClient } from 'aws-amplify/api'
import { getAnalyticsData } from '../graphql/queries'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts'
import './Analytics.css'

function Analytics({ companyId, userEmail }) {
  const [analyticsData, setAnalyticsData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const client = useMemo(() => {
    try {
      return generateClient()
    } catch (error) {
      console.error('Error initializing GraphQL client:', error)
      return null
    }
  }, [])

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!client || !companyId || !userEmail) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const response = await client.graphql({
          query: getAnalyticsData,
          variables: { companyId: companyId, userEmail: userEmail }
        })

        if (response.errors) {
          console.error('Error fetching analytics:', response.errors)
          setError(response.errors[0]?.message || 'Failed to fetch analytics data')
          return
        }

        if (response.data?.getAnalyticsData) {
          setAnalyticsData(response.data.getAnalyticsData)
        }
      } catch (err) {
        console.error('Error fetching analytics:', err)
        setError(err.message || 'Failed to load analytics')
      } finally {
        setLoading(false)
      }
    }

    fetchAnalytics()
  }, [client, companyId, userEmail])

    // Calculate resolution funnel data
  const funnelData = useMemo(() => {
    if (!analyticsData) return null

    const threads = analyticsData.threads || []
    const flags = analyticsData.flags || []

    const totalIssues = threads.length
    const resolvedByCoaching = threads.filter(t => t.pool === 'pool1' && t.status === 'resolved').length
    const requiredMediation = threads.filter(t => t.pool === 'pool2' && t.status === 'resolved').length
    const escalatedToHR = flags.length

    return {
      totalIssues,
      resolvedByCoaching,
      requiredMediation,
      escalatedToHR,
      coachingPercent: totalIssues > 0 ? Math.round((resolvedByCoaching / totalIssues) * 100) : 0,
      mediationPercent: totalIssues > 0 ? Math.round((requiredMediation / totalIssues) * 100) : 0,
      hrPercent: totalIssues > 0 ? Math.round((escalatedToHR / totalIssues) * 100) : 0
    }
  }, [analyticsData])

  // Calculate time-to-resolution distribution
  const resolutionTimeData = useMemo(() => {
    if (!analyticsData) return null

    const threads = analyticsData.threads || []
    const flags = analyticsData.flags || []

    const calculateDays = (start, end) => {
      if (!start || !end) return null
      const startDate = new Date(start)
      const endDate = new Date(end)
      return Math.round((endDate - startDate) / (1000 * 60 * 60 * 24))
    }

    const coachingTimes = threads
      .filter(t => t.pool === 'pool1' && t.status === 'resolved' && t.resolvedAt)
      .map(t => calculateDays(t.createdAt, t.resolvedAt))
      .filter(d => d !== null && d >= 0)

    const mediationTimes = threads
      .filter(t => t.pool === 'pool2' && t.status === 'resolved' && t.resolvedAt)
      .map(t => calculateDays(t.createdAt, t.resolvedAt))
      .filter(d => d !== null && d >= 0)

    const hrTimes = flags
      .filter(f => f.status === 'resolved')
      .map(f => {
        // For flags, use createdAt as resolution time (approximation)
        // In a real system, you'd have a resolvedAt field
        const created = new Date(f.createdAt)
        const now = new Date()
        return Math.round((now - created) / (1000 * 60 * 60 * 24))
      })
      .filter(d => d >= 0)

    return {
      coaching: coachingTimes,
      mediation: mediationTimes,
      hrEscalation: hrTimes
    }
  }, [analyticsData])

  // Calculate monthly issue volume and resolution rate
  const monthlyData = useMemo(() => {
    if (!analyticsData) return []

    const threads = analyticsData.threads || []
    const flags = analyticsData.flags || []

    // Group by month
    const monthlyMap = {}
    
    // Combine threads and flags
    const allItems = threads.concat(flags.map(f => {
      return {
        ...f,
        isFlag: true
      }
    }))
    
    allItems.forEach(item => {
      const date = new Date(item.createdAt)
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      
      if (!monthlyMap[monthKey]) {
        monthlyMap[monthKey] = {
          month: monthKey,
          issues: 0,
          resolved: 0
        }
      }
      
      monthlyMap[monthKey].issues++
      
      // Count as resolved if status is not 'active' or 'new'
      if (item.status && item.status !== 'active' && item.status !== 'new') {
        monthlyMap[monthKey].resolved++
      }
    })

    // Convert to array and calculate resolution rate
    return Object.values(monthlyMap)
      .map(m => ({
        ...m,
        resolutionRate: m.issues > 0 ? Math.round((m.resolved / m.issues) * 100) : 0
      }))
      .sort((a, b) => a.month.localeCompare(b.month))
  }, [analyticsData])

  // Calculate department heatmap data
  const departmentHeatmapData = useMemo(() => {
    if (!analyticsData) return []

    const threads = analyticsData.threads || []
    const flags = analyticsData.flags || []
    const departmentMap = analyticsData.departmentMap ? JSON.parse(analyticsData.departmentMap) : {}

    // Count issues by department
    const deptCounts = {}
    
    threads.forEach(thread => {
      const dept = thread.department || 'Unknown'
      deptCounts[dept] = (deptCounts[dept] || 0) + 1
    })

    flags.forEach(flag => {
      const dept = flag.department || 'Unknown'
      deptCounts[dept] = (deptCounts[dept] || 0) + 1
    })

    // Convert to array and calculate intensity
    const maxCount = Math.max(...Object.values(deptCounts), 1)
    
    return Object.entries(deptCounts)
      .map(([department, count]) => ({
        department,
        count,
        intensity: count / maxCount
      }))
      .sort((a, b) => b.count - a.count)
  }, [analyticsData])

  // Helper to get color based on intensity (sage green theme)
  const getHeatmapColor = (intensity) => {
    if (intensity === 0) return '#f0f0f0'
    if (intensity < 0.25) return '#d4e4d7' // Very light sage
    if (intensity < 0.5) return '#a8c5ab' // Light sage
    if (intensity < 0.75) return '#6b8e6f' // Medium sage
    return '#4a5f4d' // Dark sage
  }

  if (loading) {
    return (
      <div className="analytics-loading">
        <div className="loading">Loading analytics...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="analytics-error">
        <div className="error">Error: {error}</div>
      </div>
    )
  }

  if (!analyticsData) {
    return (
      <div className="analytics-empty">
        <div className="empty-state">No analytics data available</div>
      </div>
    )
  }

  return (
    <div className="analytics-container">
      <div className="analytics-wrapper">
        <div className="analytics-grid">
        {/* Chart 1: Resolution Funnel */}
        <div className="chart-card">
          <h3>Resolution Funnel</h3>
          {funnelData && (
            <div className="funnel-diagram">
              <div className="funnel-step">
                <div className="funnel-number">{funnelData.totalIssues}</div>
                <div className="funnel-label">Issues Raised</div>
              </div>
              <div className="funnel-arrow">↓</div>
              <div className="funnel-branches">
                <div className="funnel-branch green">
                  <div className="funnel-number">{funnelData.resolvedByCoaching}</div>
                  <div className="funnel-label">Resolved by AI Coaching</div>
                  <div className="funnel-percent">({funnelData.coachingPercent}%)</div>
                </div>
                <div className="funnel-branch yellow">
                  <div className="funnel-number">{funnelData.requiredMediation}</div>
                  <div className="funnel-label">Required Mediation</div>
                  <div className="funnel-percent">({funnelData.mediationPercent}%)</div>
                </div>
                <div className="funnel-branch red">
                  <div className="funnel-number">{funnelData.escalatedToHR}</div>
                  <div className="funnel-label">Escalated to HR</div>
                  <div className="funnel-percent">({funnelData.hrPercent}%)</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Chart 2: Time-to-Resolution Distribution */}
        <div className="chart-card">
          <h3>Time-to-Resolution Distribution</h3>
          {resolutionTimeData && (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={[
                {
                  category: 'AI Coaching',
                  min: resolutionTimeData.coaching.length > 0 ? Math.min(...resolutionTimeData.coaching) : 0,
                  max: resolutionTimeData.coaching.length > 0 ? Math.max(...resolutionTimeData.coaching) : 0,
                  avg: resolutionTimeData.coaching.length > 0 
                    ? Math.round(resolutionTimeData.coaching.reduce((a, b) => a + b, 0) / resolutionTimeData.coaching.length)
                    : 0,
                  count: resolutionTimeData.coaching.length
                },
                {
                  category: 'Mediation',
                  min: resolutionTimeData.mediation.length > 0 ? Math.min(...resolutionTimeData.mediation) : 0,
                  max: resolutionTimeData.mediation.length > 0 ? Math.max(...resolutionTimeData.mediation) : 0,
                  avg: resolutionTimeData.mediation.length > 0
                    ? Math.round(resolutionTimeData.mediation.reduce((a, b) => a + b, 0) / resolutionTimeData.mediation.length)
                    : 0,
                  count: resolutionTimeData.mediation.length
                },
                {
                  category: 'HR Escalation',
                  min: resolutionTimeData.hrEscalation.length > 0 ? Math.min(...resolutionTimeData.hrEscalation) : 0,
                  max: resolutionTimeData.hrEscalation.length > 0 ? Math.max(...resolutionTimeData.hrEscalation) : 0,
                  avg: resolutionTimeData.hrEscalation.length > 0
                    ? Math.round(resolutionTimeData.hrEscalation.reduce((a, b) => a + b, 0) / resolutionTimeData.hrEscalation.length)
                    : 0,
                  count: resolutionTimeData.hrEscalation.length
                }
              ]}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" />
                <YAxis label={{ value: 'Days', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="min" fill="#a8c5ab" name="Min Days" />
                <Bar dataKey="avg" fill="#6b8e6f" name="Avg Days" />
                <Bar dataKey="max" fill="#4a5f4d" name="Max Days" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Chart 3: Monthly Issue Volume + Resolution Rate */}
        <div className="chart-card">
          <h3>Monthly Issue Volume + Resolution Rate</h3>
          {monthlyData.length > 0 && (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis yAxisId="left" label={{ value: 'Issues', angle: -90, position: 'insideLeft' }} />
                <YAxis yAxisId="right" orientation="right" label={{ value: 'Resolution Rate %', angle: 90, position: 'insideRight' }} />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="issues" fill="#6b8e6f" name="Issues Raised" />
                <Line yAxisId="right" type="monotone" dataKey="resolutionRate" stroke="#4a5f4d" strokeWidth={2} name="Resolution Rate %" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Chart 4: Department Heatmap Grid */}
        <div className="chart-card">
          <h3>Department Heatmap Grid</h3>
          {departmentHeatmapData.length > 0 && (
            <div className="heatmap-grid">
              {departmentHeatmapData.map(({ department, count, intensity }) => (
                <div
                  key={department}
                  className="heatmap-tile"
                  style={{ backgroundColor: getHeatmapColor(intensity) }}
                  title={`${department}: ${count} issues`}
                >
                  <div className="heatmap-dept">{department}</div>
                  <div className="heatmap-count">{count}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  )
}

export default Analytics

